import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Menu, ChevronRight, Building2, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { apiClient } from '@/services/api/apiClient';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/components/ui/use-toast';

interface Location {
  rest_id: number;
  rest_nm: string;
  location_id: number;
  is_current: boolean;
  is_primary: boolean;
  location_status: string;
  address?: string;
  city?: string;
  state?: string;
  total_menu_items: number;
  menu_id?: number;
}

interface LocationSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  action: 'edit' | 'preview' | 'switch';
  aiMode?: boolean;
  onLocationSwitched?: () => void;
}

const LocationSelectorModal = ({ 
  isOpen, 
  onClose, 
  action, 
  aiMode = false,
  onLocationSwitched 
}: LocationSelectorModalProps) => {
  console.log('🎯 LocationSelectorModal rendered', { isOpen, action, aiMode });
  
  const navigate = useNavigate();
  const { toast } = useToast();
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    if (isOpen) {
      console.log('🎯 Modal opened, fetching locations...');
      setLoading(true); // Reset loading state
      setSwitching(false); // Reset switching state
      fetchLocations();
    } else {
      // Reset state when modal closes
      setSwitching(false);
      setLocations([]);
    }
  }, [isOpen]);
  
  // Debug state changes
  useEffect(() => {
    console.log('📊 Modal state updated:', {
      isOpen,
      loading,
      locationsCount: locations.length,
      locations
    });
  }, [isOpen, loading, locations]);

  const fetchLocations = async () => {
    try {
      console.log('📡 Fetching locations from /api/locations...');
      const response = await apiClient.get('/locations');
      console.log('📍 Modal fetched locations:', response.data);
      // The endpoint returns an object with locations array
      const locationsList = response.data.locations || [];
      setLocations(locationsList);
      console.log('📍 Set locations to:', locationsList);
    } catch (error) {
      console.error('❌ Failed to fetch locations:', error);
      toast({
        title: "Error",
        description: "Failed to load locations",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLocationSwitch = async (location: Location) => {
    console.log('Location switch requested:', {
      location,
      action,
      aiMode,
      is_current: location.is_current,
      menu_id: location.menu_id,
      total_items: location.total_menu_items
    });

    // If current location and we're trying to edit/preview, just navigate
    if (location.is_current && (action === 'edit' || action === 'preview')) {
      console.log('🎯 Current location selected for', action);
      
      // Close modal first to prevent state issues
      onClose();
      
      // Small delay to ensure modal closes properly
      setTimeout(() => {
        if (action === 'edit') {
          if (location.total_menu_items > 0 && location.menu_id) {
            // Navigate to menu review for current location
            const url = `/restaurant-menu-review?processing_id=${location.menu_id}${aiMode ? '&aiMode=true' : ''}`;
            console.log('Navigating to:', url);
            navigate(url);
          } else {
            console.log('No menu items, navigating to upload');
            navigate('/restaurant-menu-upload');
          }
        } else if (action === 'preview' && location.total_menu_items > 0 && location.menu_id) {
          const url = `/menu-preview?menu_id=${location.menu_id}`;
          console.log('Navigating to preview:', url);
          navigate(url);
        }
      }, 100);
      
      return;
    }
    
    // If just switching and already current, show info
    if (location.is_current && action === 'switch') {
      toast({
        title: "Info",
        description: "This is already your current location",
      });
      return;
    }

    setSwitching(true);
    try {
      await apiClient.post('/locations/switch', {
        rest_id: location.rest_id
      });

      toast({
        title: "Success",
        description: `Switched to ${location.rest_nm}`,
      });

      // Small delay to ensure backend has updated the current location
      await new Promise(resolve => setTimeout(resolve, 500));

      // Close modal first
      onClose();

      // Notify parent component to refresh data
      if (onLocationSwitched) {
        await onLocationSwitched();
      }

      // Add another small delay to ensure state updates have propagated
      await new Promise(resolve => setTimeout(resolve, 100));

      // If we're switching locations for edit/preview, navigate appropriately
      console.log('Post-switch navigation:', { action, menu_id: location.menu_id, total_items: location.total_menu_items });
      
      if (action === 'edit') {
        if (location.total_menu_items > 0 && location.menu_id) {
          // Navigate directly to the menu review page
          const url = `/restaurant-menu-review?processing_id=${location.menu_id}${aiMode ? '&aiMode=true' : ''}`;
          console.log('Navigating after switch to:', url);
          navigate(url);
        } else {
          console.log('No menu items after switch, navigating to upload');
          navigate('/restaurant-menu-upload');
        }
      } else if (action === 'preview' && location.total_menu_items > 0 && location.menu_id) {
        // Navigate directly to the menu preview
        const url = `/menu-preview?menu_id=${location.menu_id}`;
        console.log('Navigating to preview after switch:', url);
        navigate(url);
      }
    } catch (error) {
      console.error('Failed to switch location:', error);
      toast({
        title: "Error",
        description: "Failed to switch location",
        variant: "destructive"
      });
    } finally {
      setSwitching(false);
    }
  };

  const getActionText = () => {
    if (action === 'switch') return 'switch to';
    if (action === 'edit') return aiMode ? 'generate AI images for' : 'edit menu at';
    return 'preview menu at';
  };

  const getActionTitle = () => {
    if (action === 'switch') return 'Switch Location';
    if (action === 'edit') return aiMode ? 'Generate AI Images' : 'Edit Menu';
    return 'Preview Menu';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-gradient-to-b from-[#1f4ed1] to-[#2256e5] border-white/20 shadow-2xl shadow-black/50 backdrop-blur-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <Building2 className="h-5 w-5 text-yellow-400" />
            {getActionTitle()}
          </DialogTitle>
          <DialogDescription className="text-white/80">
            Choose which location to {getActionText()}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-2">
          {loading ? (
            <div className="text-center py-8 text-white/70 flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin text-yellow-400" />
              Loading locations...
            </div>
          ) : locations.length === 0 ? (
            <div className="text-center py-8 text-white/70">
              No locations found
            </div>
          ) : (
            locations.map((location, index) => (
              <motion.div
                key={location.rest_id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05, duration: 0.3 }}
              >
                <button
                  onClick={() => handleLocationSwitch(location)}
                  className={`w-full text-left p-4 rounded-lg border transition-all group backdrop-blur-sm ${
                    location.is_current
                      ? 'border-yellow-400/50 bg-yellow-400/20'
                      : 'border-white/20 bg-white/10 hover:border-yellow-400/30 hover:bg-white/20'
                  }`}
                  disabled={switching || (action === 'preview' && location.total_menu_items === 0)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium text-white group-hover:text-yellow-400 transition-colors">
                          {location.rest_nm}
                        </h4>
                        {location.is_current && (
                          <span className="text-xs bg-yellow-400 text-gray-900 font-medium px-2 py-0.5 rounded-full">
                            Current
                          </span>
                        )}
                        {location.is_primary && (
                          <span className="text-xs bg-white/20 text-white px-2 py-0.5 rounded-full">
                            Primary
                          </span>
                        )}
                        {location.location_id > 1 && (
                          <span className="text-xs bg-white/20 text-white px-2 py-0.5 rounded-full">
                            Location #{location.location_id}
                          </span>
                        )}
                        {location.location_status !== 'active' && (
                          <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded-full border border-red-500/30">
                            {location.location_status === 'inactive' ? 'Temporarily Closed' : 'Closed'}
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm text-white/70">
                        <div className="flex items-center gap-1">
                          <MapPin className="h-3 w-3 text-yellow-400/70" />
                          <span>{location.city}, {location.state}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Menu className="h-3 w-3 text-yellow-400/70" />
                          <span>
                            {location.total_menu_items > 0
                              ? `${location.total_menu_items} items`
                              : action === 'edit' ? 'No menu - click to upload' : 'No menu to preview'
                            }
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {!location.is_current && (
                      <ChevronRight className="h-5 w-5 text-white/40 group-hover:text-yellow-400 mt-1 transition-colors" />
                    )}
                  </div>
                </button>
              </motion.div>
            ))
          )}
        </div>

        {switching && (
          <div className="absolute inset-0 bg-[#1f4ed1]/90 backdrop-blur-sm flex items-center justify-center rounded-lg">
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin text-yellow-400" />
              <span className="text-sm text-white">Switching location...</span>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LocationSelectorModal;